import type { GpsPoint } from "./sessionDetail";

/**
 * Wire types + pure reducers for the dashboard live channel (`/ws/dashboard`).
 *
 * The same socket backs the field-support web app and the crew mobile app; this
 * is Command's client-side half. Everything here is pure — no sockets, no React
 * — so the merge rules can be reasoned about (and tested) on their own. The I/O
 * lives in `src/live/DashboardSocketContext.tsx`.
 *
 * Frames, and what each is allowed to touch:
 *   snapshot     → re-seeds the whole fleet from durable SQL truth (on connect
 *                  AND on every reconnect, which is what makes a drop self-heal)
 *   robot_state  → overlays live operational fields onto one fleet entry
 *   session      → a *signal only*: the durable session row changed, go refetch.
 *                  Never painted directly — the payload isn't the data.
 *   telemetry    → high-rate, per-device, and only after an explicit subscribe.
 *                  Feeds one open session view; never touches the fleet.
 *   subscribed / error → acks for the subscribe commands.
 *
 * Notifications are deliberately not handled: Command has no in-app alert
 * surface, so those frames are ignored rather than half-consumed.
 */

// ---- Wire shapes -----------------------------------------------------------

/** Durable per-device status, as the snapshot carries it (mirrors MowerStatus). */
export interface SnapshotMowerStatus {
  device_id: string;
  last_update: string;
  latitude: number;
  longitude: number;
  gps_hdop: number;
  gps_h_accuracy: number;
  battery_charge_percent: number;
  is_ready_for_autonomy: boolean;
  has_gps_lock: boolean;
  status_message: string;
  software_version?: string | null;
  /** The run this device is currently on, and its state. These are what make a
   *  live session visible at all: `/kpis/sessions` inner-joins SessionKPIs,
   *  which don't exist until a session finalises, so a running session is
   *  unreachable over REST. The snapshot announces it directly. */
  trial_number?: string | null;
  session_status?: string | null;
  session_start_time?: string | null;
  session_end_time?: string | null;
  address?: string | null;
  display_name?: string | null;
}

export interface SnapshotFrame {
  type: "snapshot";
  data: Record<string, SnapshotMowerStatus>;
}

/** The operational fields a robot_state delta may carry. Every one is optional:
 *  a delta reports what changed, not the whole device. */
export interface RobotStatePayload {
  device_id: string;
  watchdog_status?: string;
  auto_manual_status?: string;
  robot_state?: string;
  mow_trial?: string;
  readiness_text?: string;
  pause_cause?: string;
  robot_time?: string;
  gps_hdop?: number;
  gps_h_accuracy?: number;
  perimeter_status?: string;
  /** Delta-sourced; unknown until the first robot_state delta arrives. */
  connected?: boolean;
}

interface DeltaEnvelope {
  type: string;
  device_id: string;
  trial_number: string;
  correlation_id: string;
  /** ISO-8601. */
  timestamp: string;
  payload: unknown;
}

export interface RobotStateFrame extends DeltaEnvelope {
  type: "robot_state";
  payload: RobotStatePayload;
}

/** Legacy per-change frame from the local-broadcast path, used when the Redis
 *  backplane is off (`REDIS_ENABLED=false`). Production runs with it on and
 *  sends `robot_state`; this keeps a local backend usable. Note the device id
 *  lives inside `data` here — there is no envelope. */
export interface LegacyUpdateFrame {
  type: "update";
  data: RobotStatePayload;
}

export interface SessionFrame extends DeltaEnvelope {
  type: "session";
}

export interface TelemetryFrame extends DeltaEnvelope {
  type: "telemetry";
}

export interface SubscribedFrame {
  type: "subscribed";
  accepted: string[];
  rejected: string[];
}

export interface ErrorFrame {
  type: "error";
  code: "subscribe_rejected";
  message: string;
}

export interface SubscribeCommand {
  action: "subscribe" | "unsubscribe";
  devices: string[];
}

export const subscribeCommand = (deviceId: string): SubscribeCommand => ({
  action: "subscribe",
  devices: [deviceId],
});

export const unsubscribeCommand = (deviceId: string): SubscribeCommand => ({
  action: "unsubscribe",
  devices: [deviceId],
});

// ---- Fleet tier ------------------------------------------------------------

/** One device's live fleet state: the durable snapshot with any robot_state
 *  delta overlaid. `connected` is delta-sourced, so `undefined` means "not yet
 *  known", which is a different thing from `false`. */
export type MergedMowerState = SnapshotMowerStatus & Partial<RobotStatePayload>;

export type FleetState = Record<string, MergedMowerState>;

export type FleetFrame = SnapshotFrame | RobotStateFrame | LegacyUpdateFrame;

export function reduceFleet(state: FleetState, frame: FleetFrame): FleetState {
  if (frame.type === "snapshot") {
    // A (re)connect snapshot re-seeds from durable truth. Rebuilt rather than
    // merged, so a device that left the authorized set actually disappears —
    // and `connected` drops back to unknown until the next delta says otherwise.
    const next: FleetState = {};
    for (const [deviceId, status] of Object.entries(frame.data ?? {})) {
      next[deviceId] = { ...status };
    }
    return next;
  }

  if (frame.type === "robot_state" || frame.type === "update") {
    // A robot_state frame names the device twice — on the envelope (what the
    // server routes and authz-filters on) and again inside the payload. Prefer
    // the envelope; the legacy frame only has the payload's.
    const payload =
      frame.type === "robot_state" ? frame.payload : frame.data;
    const deviceId =
      (frame.type === "robot_state" ? frame.device_id : undefined) ??
      payload?.device_id;
    if (!deviceId) return state;

    const existing = state[deviceId];
    // Drop-if-unknown: the snapshot defines the fleet. A delta for a device
    // outside it is ignored; the reconnect re-snapshot heals a late arrival.
    if (existing === undefined) return state;
    // Overlay only the keys the delta actually carries, so snapshot-only fields
    // (battery, versions, coordinates) are never blanked by a partial delta.
    const overlay: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(payload ?? {})) {
      if (v !== undefined) overlay[k] = v;
    }
    return { ...state, [deviceId]: { ...existing, ...overlay } };
  }

  return state;
}

// ---- Session tier ----------------------------------------------------------

/** A "go refetch device X" trigger derived from `session` deltas. The delta
 *  carries no session data worth painting — it only says SQL changed — so this
 *  is a monotone nonce a consumer effect watches, not state to render. */
export interface SessionRefetchSignal {
  device_id: string | null;
  seq: number;
}

export const initialSessionRefetchSignal: SessionRefetchSignal = {
  device_id: null,
  seq: 0,
};

export function reduceSessionRefetch(
  prev: SessionRefetchSignal,
  frame: SessionFrame,
  knownDevices: ReadonlySet<string>,
): SessionRefetchSignal {
  // Same drop-if-unknown rule as robot_state — defense in depth behind the
  // server's own authorization filter. Returning `prev` by reference matters:
  // a consuming effect keyed on this object then never fires for a dropped one.
  if (!knownDevices.has(frame.device_id)) return prev;
  return { device_id: frame.device_id, seq: prev.seq + 1 };
}

// ---- Telemetry tier --------------------------------------------------------

/** Subscribe/unsubscribe outcome. `error` holds the deliberately vague
 *  rejection frame — the server refuses to distinguish "not yours" from "no
 *  such device", so a client can't enumerate devices by probing. */
export interface SubscriptionState {
  lastAccepted: string[];
  lastRejected: string[];
  error: ErrorFrame | null;
}

export const initialSubscriptionState: SubscriptionState = {
  lastAccepted: [],
  lastRejected: [],
  error: null,
};

export type SubscriptionControlFrame = SubscribedFrame | ErrorFrame;

export function reduceSubscription(
  state: SubscriptionState,
  frame: SubscriptionControlFrame,
): SubscriptionState {
  if (frame.type === "subscribed") {
    return {
      lastAccepted: frame.accepted,
      lastRejected: frame.rejected,
      // A clean ack clears a stale error; an ack carrying rejections leaves it
      // to the `error` frame that follows, so the failure survives in state.
      error: frame.rejected.length === 0 ? null : state.error,
    };
  }
  if (frame.type === "error") return { ...state, error: frame };
  return state;
}

/** The telemetry payload, as `convert_telemetry_to_dict` builds it. The
 *  envelope types it as opaque (it is the robot's frame verbatim), so this is
 *  the narrowing we render through. Every field is optional: a frame carries
 *  what the robot reported and older firmware reports less. Notably the
 *  position block only rides on frames with a ModeConsistent sub-message, so
 *  many frames carry readings but no coordinates. */
export interface TelemetryPayloadFields {
  device_id?: string;
  timestamp?: string;
  // Position
  latitude?: number;
  longitude?: number;
  gps_h_accuracy?: number;
  gps_hdop?: number;
  /** Fix-quality enum — `int32` on the wire, not a label. */
  gps_status?: number;
  gps_fix_status?: number | null;
  // Motion
  planar_state?: string;
  cmd_velocity_x?: number;
  cmd_velocity_theta?: number;
  zlac_velocity_x?: number;
  zlac_velocity_theta?: number;
  wheel_slip_detected?: boolean;
  num_steps_left?: number;
  // Power
  battery_charge_percent?: number;
  battery_voltage?: number;
  bus_voltage?: number;
  current_consumed?: number;
  left_motor_current?: number;
  right_motor_current?: number;
  // Blade / motors
  blade_rpm?: number | null;
  blade_toggle_status?: boolean | null;
  left_motor_faults?: string;
  right_motor_faults?: string;
  // Temperatures, °C
  cpu_temperature?: number;
  driver_temperature?: number;
  blade_motor_temperature?: number;
  left_motor_temperature?: number;
  right_motor_temperature?: number;
  // Session progress
  mowed_area?: number;
  mowed_duration?: number;
  certainty_value?: number;
  // Causes
  pause_cause?: string;
  active_pause_conditions?: string;
  ext_estop_cause?: string;
  event_cause?: string;
  is_autonomous?: boolean;
  hardware_event?: string;
}

/** The merged view of every frame so far: the newest value for each field.
 *  Merged rather than last-frame-only because frames are sparse — showing only
 *  what the newest one happened to contain would blank most of the panel on
 *  most frames. */
export type LiveReading = TelemetryPayloadFields;

function readingFrom(
  prev: LiveReading | null,
  p: TelemetryPayloadFields,
): LiveReading {
  // Same overlay rule as the fleet reducer: take only keys this frame actually
  // carries. An empty string counts as carried — that is how a cause field says
  // "cleared", and dropping it would pin a stale fault on screen forever.
  const next: Record<string, unknown> = { ...(prev ?? {}) };
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== null) next[k] = v;
  }
  return next as LiveReading;
}

export interface LiveTelemetryState {
  /** Which device this path belongs to. */
  deviceId: string | null;
  /** Points accumulated frame by frame, in the same shape the REST path uses so
   *  the live map and the replayed map render through identical code. */
  path: GpsPoint[];
  /** Wall-clock ms of the most recent frame — the staleness clock's input. */
  lastFrameMs: number | null;
  /** Latest scalar readings, or null before the first frame. */
  reading: LiveReading | null;
}

export const initialLiveTelemetryState: LiveTelemetryState = {
  deviceId: null,
  path: [],
  lastFrameMs: null,
  reading: null,
};

export function reduceTelemetryPath(
  state: LiveTelemetryState,
  frame: TelemetryFrame,
  watchedDeviceId: string,
  nowMs: number,
): LiveTelemetryState {
  // Gate on the envelope's routing device_id — a frame for anything else is
  // returned by reference so a consuming effect never churns.
  if (frame.device_id !== watchedDeviceId) return state;

  // Switching device starts a fresh path; the previous robot's trail must never
  // bleed into this one's map.
  const base =
    state.deviceId === watchedDeviceId ? state : initialLiveTelemetryState;

  const payload = (frame.payload ?? {}) as TelemetryPayloadFields;

  const reading = readingFrom(base.reading, payload);

  // A frame without coordinates still advances the readings and the liveness
  // clock — it just plots no point. Those frames are common, so treating them
  // as nothing would throw away most of the telemetry.
  if (
    typeof payload.latitude !== "number" ||
    typeof payload.longitude !== "number"
  ) {
    return {
      deviceId: watchedDeviceId,
      path: base.path,
      lastFrameMs: nowMs,
      reading,
    };
  }

  const parsed = Date.parse(frame.timestamp || payload.timestamp || "");
  const point: GpsPoint = {
    latitude: payload.latitude,
    longitude: payload.longitude,
    h_acc: payload.gps_h_accuracy ?? 0.5,
    // Fall back to arrival time so a point always sorts against the REST-loaded
    // history; a NaN timestamp would break the scrubber's cutoff search.
    timestamp: Number.isNaN(parsed) ? nowMs : parsed,
    bladesActive:
      payload.blade_toggle_status ?? (payload.blade_rpm ?? 0) > 0,
  };

  return {
    deviceId: watchedDeviceId,
    path: [...base.path, point],
    lastFrameMs: nowMs,
    reading,
  };
}

/** True when the session is running but the channel has gone quiet. The robot
 *  emits at ~1 Hz, so 30s of silence is well past a transient gap — it means
 *  the run is nominally live but nothing is actually arriving. */
export const STALE_LIVE_THRESHOLD_MS = 30_000;

// ---- Live sessions ---------------------------------------------------------

/** Devices the snapshot reports as mid-run, newest first.
 *
 *  The live channel is the only place a running session is visible: the KPI
 *  endpoint joins through SessionKPIs, and those rows are written when a
 *  session finalises. So rather than asking the server for live sessions, read
 *  them off the fleet state the socket already maintains. */
export function liveSessionsFromFleet(
  fleet: FleetState,
): { device_id: string; trial_number: string; start_time: string | null }[] {
  return Object.values(fleet)
    .filter((m) => m.session_status === "in_progress" && !!m.trial_number)
    .map((m) => ({
      device_id: m.device_id,
      trial_number: m.trial_number as string,
      start_time: m.session_start_time ?? null,
    }))
    .sort((a, b) => (b.start_time ?? "").localeCompare(a.start_time ?? ""));
}
